/**
 * Text normalisation helpers for fields with cross-tier hygiene rules.
 *
 * Spec §5.1 requires Project Number to be "case-insensitive, trimmed". §4.1
 * applies the same pattern to Supplier names; §4.2 to Client names; §4.3 to
 * Department names. These helpers centralise the trim concept so a future
 * normalisation tweak (e.g., collapsing internal whitespace, or NFKC) is a
 * single-line change in this file instead of a sprawl across handlers.
 *
 * Used by:
 *   - apps/api route handlers — before pre-check and insert/update
 *   - apps/web new-project / master modals — before mutation submit
 *
 * Both sides apply the same normalisation so the case-insensitive uniqueness
 * pre-check and the DB unique index (which uses lower(btrim(...))) agree on
 * what counts as "the same name". A mismatch between the two would surface
 * as a confusing 409 from the index rather than the clean ConflictError the
 * pre-check raises.
 */

/**
 * Normalise a name-like field by trimming surrounding whitespace.
 *
 * Applied to: supplier name, client name, department name, project title.
 *
 * Leaves internal whitespace intact — "Acme Corp" stays "Acme Corp", not
 * "AcmeCorp". The spec only calls out surrounding whitespace.
 */
export function normaliseName(value: string): string {
  return value.trim();
}

/**
 * Normalise a project number by trimming surrounding whitespace.
 *
 * Project numbers are typically alphanumeric (e.g. "PAF-24-018"); the spec
 * doesn't impose a stricter format. We mirror the name normalisation rather
 * than uppercasing or stripping internal spaces, because:
 *   - The DB unique index uses lower(btrim(project_number)), so case is
 *     handled at comparison time and we don't need to pre-uppercase here.
 *   - Internal whitespace is unusual but not illegal per spec; if a user
 *     enters "PAF 24 018" deliberately, we keep it.
 */
export function normaliseProjectNumber(value: string): string {
  return value.trim();
}

/**
 * Normalise an incoterm / terms-of-delivery code.
 *
 * Incoterms are short business codes (FOR, FOB, CIF, DAP, etc.). Store and
 * compare them uppercase so settings, project forms, and validation remain
 * consistent regardless of user input casing.
 */
export function normaliseIncotermCode(value: string): string {
  return value.trim().toLocaleUpperCase('en-US');
}

/**
 * Uppercase controlled business text for UAT display/input normalisation.
 *
 * Use for business labels and entries such as supplier names, item descriptions,
 * UOM, origin, make/brand, inspection authority, and remarks where the client
 * expects uppercase entries.
 *
 * Do not apply blindly to emails, URLs, file names, or opaque technical tokens
 * where casing may be meaningful.
 */
export function uppercaseBusinessText(value: string | null | undefined): string {
  if (value == null) return '';
  return value.trim().toLocaleUpperCase('en-US');
}

/**
 * Display helper for optional uppercase business entries.
 */
export function formatUppercaseBusinessText(value: string | null | undefined): string {
  const upper = uppercaseBusinessText(value);
  return upper === '' ? '—' : upper;
}
